import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Check,
  ChevronDown,
  Copy,
  Database,
  History,
  Plus,
  Send,
  Square,
  Trash2,
  Wrench,
  X,
} from 'lucide-react';

import { api, ApiError, isAbortError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';
import { AnswerBody } from '../components/AnswerBody';
import { EmptyState, Panel } from '../components/ui';

/**
 * Genie — the console's conversational surface.
 *
 * ── Why this does not look like a chat app ────────────────────────────────
 *
 * It used to. Right-aligned accent bubbles, a centred sparkle over rounded
 * suggestion cards, one drop-shadowed card per reply: the conventions of a
 * consumer chat product, which is a different product with different values.
 * Dropped into an operations console they read as borrowed, because they are —
 * `qs-panel` appeared exactly once on the whole page while every other screen in
 * QuickStock is built from hairline rules, mono figures and eyebrow labels.
 *
 * So an exchange here is not two speech bubbles. It is a query and its readout,
 * which is the vernacular of a shop that runs on receipts and reports: the
 * question is marked by an accent rule in the margin and set as the heading of
 * its exchange, the answer sits plainly on the page beneath it, and the panel —
 * the console's container for data — is spent on the one thing that actually is
 * data, the working.
 *
 * The measure is capped at 68 characters. An answer is prose, and prose running
 * the full width of a desktop console is unreadable no matter how good the type
 * is.
 */

interface Step {
  tool: string;
  args: Record<string, unknown>;
  ok: boolean;
  sql?: string;
  tables?: string[];
  rowCount?: number;
  error?: string;
  ms: number;
}

interface Turn {
  /** Stable across the pending-to-answered swap, so React keeps its place. */
  id: string;
  role: 'user' | 'assistant';
  content: string;
  steps?: Step[];
  /** Set while a turn is in flight, so it can render as pending. */
  pending?: boolean;
}

interface Conversation {
  conversationId: string;
  title: string | null;
  lastMessageAt: string;
}

interface AskResponse {
  conversationId: string;
  answer: string;
  steps: Step[];
  usage: { promptTokens: number; completionTokens: number; usdSpent: string };
}

interface GenieConfig {
  available: boolean;
  monthlyCapUsd: number;
}

const SUGGESTIONS = [
  'How did we do this week compared with last?',
  'Which products need reordering?',
  'Who owes us the most, and how overdue are they?',
  'What are our busiest hours?',
];

let turnCounter = 0;
const nextTurnId = (): string => `turn-${(turnCounter += 1)}`;

export function Genie(): React.JSX.Element {
  const { activeStore } = useAuth();
  const storeId = activeStore?.storeId;
  const storeName = activeStore?.displayName ?? 'this shop';
  const queryClient = useQueryClient();

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [question, setQuestion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const endRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const { data: genieConfig } = useQuery({
    queryKey: ['genie-config'],
    queryFn: () => api.get<GenieConfig>('/genie/config'),
  });

  const { data: conversations } = useQuery({
    queryKey: ['genie-conversations', storeId],
    queryFn: () =>
      api.get<{ conversations: Conversation[] }>(`/stores/${storeId}/genie/conversations`),
    enabled: Boolean(storeId),
  });

  const { data: spend } = useQuery({
    queryKey: ['genie-usage', storeId],
    queryFn: () =>
      api.get<{ usdSpent: number; capUsd: number; capReached: boolean }>(
        `/stores/${storeId}/genie/usage`,
      ),
    enabled: Boolean(storeId),
  });

  // Switching store must not carry a conversation across — the conversation
  // belongs to one shop, and the server would 404 it anyway.
  useEffect(() => {
    setConversationId(null);
    setTurns([]);
    setError(null);
    setNotice(null);
  }, [storeId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns]);

  // Abandoning the page mid-question should not leave a request listening for a
  // component that no longer exists.
  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useMutation({
    mutationFn: (asked: string) => {
      const controller = new AbortController();
      abortRef.current = controller;

      return api.post<AskResponse>(
        `/stores/${storeId}/genie/ask`,
        { question: asked, ...(conversationId ? { conversationId } : {}) },
        { signal: controller.signal },
      );
    },
    onSuccess: (result) => {
      setConversationId(result.conversationId);
      setTurns((current) => [
        ...current.filter((turn) => !turn.pending),
        {
          id: nextTurnId(),
          role: 'assistant',
          content: result.answer,
          steps: result.steps,
        },
      ]);
      void queryClient.invalidateQueries({ queryKey: ['genie-conversations', storeId] });
      void queryClient.invalidateQueries({ queryKey: ['genie-usage', storeId] });
      // Back to the composer, so a follow-up needs no click.
      composerRef.current?.focus();
    },
    onError: (caught: unknown) => {
      setTurns((current) => current.filter((turn) => !turn.pending));

      // Stopping is deliberate, so it is not an error. It is also not a
      // cancellation: the question had already reached the model, which will
      // finish and store its answer regardless — saying otherwise would be a
      // convenient lie that the conversation history immediately contradicts.
      if (isAbortError(caught)) {
        setNotice('Stopped waiting. Genie may still finish this one and file it here.');
        return;
      }

      setError(
        caught instanceof ApiError ? caught.message : 'Something went wrong. Try asking again.',
      );
    },
    onSettled: () => {
      abortRef.current = null;
    },
  });

  /**
   * Deleting a conversation.
   *
   * Optimistic, and the reason is measurable rather than aesthetic: the previous
   * version awaited the DELETE and *then* invalidated the list, so the row sat
   * there for a round trip plus a refetch — two sequential requests — before it
   * disappeared. On anything but a local API that reads as a broken button, and
   * the natural response is to click it again.
   *
   * The row now goes immediately and the cache is repaired on failure. The
   * refetch still happens, but afterwards, where nobody is waiting on it.
   */
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/stores/${storeId}/genie/conversations/${id}`),

    onMutate: async (id: string) => {
      const key = ['genie-conversations', storeId];
      // Any refetch already in flight would land after this and put the row
      // back, so it is cancelled before the cache is edited.
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<{ conversations: Conversation[] }>(key);
      queryClient.setQueryData<{ conversations: Conversation[] }>(key, (current) =>
        current
          ? {
              conversations: current.conversations.filter(
                (conversation) => conversation.conversationId !== id,
              ),
            }
          : current,
      );

      // Clearing the thread is part of the optimistic step: leaving a deleted
      // conversation on screen is worse than leaving its row in the list.
      if (id === conversationId) {
        setConversationId(null);
        setTurns([]);
        setError(null);
        setNotice(null);
      }

      return { previous, key };
    },

    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(context.key, context.previous);
      setError('That conversation could not be deleted. It has been put back.');
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['genie-conversations', storeId] });
    },
  });

  function startNew(): void {
    setConversationId(null);
    setTurns([]);
    setError(null);
    setNotice(null);
    setHistoryOpen(false);
    composerRef.current?.focus();
  }

  function submit(asked: string): void {
    const trimmed = asked.trim();
    if (!trimmed || send.isPending) return;

    setError(null);
    setNotice(null);
    setQuestion('');
    setTurns((current) => [
      ...current,
      { id: nextTurnId(), role: 'user', content: trimmed },
      { id: nextTurnId(), role: 'assistant', content: '', pending: true },
    ]);
    send.mutate(trimmed);
  }

  async function openConversation(id: string): Promise<void> {
    setError(null);
    setNotice(null);
    setHistoryOpen(false);

    try {
      const { messages } = await api.get<{
        messages: { role: string; content: string | null; steps: Step[] | null }[];
      }>(`/stores/${storeId}/genie/conversations/${id}`);

      setConversationId(id);
      setTurns(
        messages
          .filter((message) => message.role === 'user' || message.role === 'assistant')
          .map((message) => ({
            id: nextTurnId(),
            role: message.role as 'user' | 'assistant',
            content: message.content ?? '',
            ...(message.steps ? { steps: message.steps } : {}),
          })),
      );
    } catch (caught) {
      // Previously this rejected into nothing and the row simply did not open,
      // which is indistinguishable from a dead button.
      setError(
        caught instanceof ApiError ? caught.message : 'That conversation could not be opened.',
      );
    }
  }

  if (genieConfig && !genieConfig.available) {
    return (
      <>
        <Header />
        <Panel>
          <EmptyState>
            Genie is not set up on this server. An OpenAI API key needs to be configured before
            it can answer anything.
          </EmptyState>
        </Panel>
      </>
    );
  }

  const history = (
    <ConversationList
      conversations={conversations?.conversations ?? []}
      activeId={conversationId}
      onOpen={(id) => void openConversation(id)}
      onDelete={(id) => remove.mutate(id)}
      deleting={remove.isPending}
    />
  );

  const spendLine = spend ? (
    <p className="text-2xs text-ink-faint">
      <span className="qs-figure">{formatSpend(spend.usdSpent)}</span> of{' '}
      <span className="qs-figure">${spend.capUsd.toFixed(2)}</span> used this month
    </p>
  ) : null;

  return (
    /*
     * Genie is the one page that fills the viewport rather than growing with
     * its content — the composer has to stay at the bottom. That makes its
     * height a sum of the chrome around it, and the sum was wrong: header
     * (3.125rem, including the brand band) + main's pt-5 (1.25rem) + its bottom
     * padding. It overflowed by about 2.4rem, which pushed the disclaimer under
     * the fold on a laptop.
     */
    <div className="flex h-[calc(100dvh-10.375rem)] flex-col md:h-[calc(100dvh-6.375rem)]">
      <Header
        action={
          /* Below `lg` the conversation column is not rendered at all, so
             without this every past conversation was unreachable on a phone —
             the device this is most likely to be opened on. */
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="flex items-center gap-1.5 rounded border border-rule px-2.5 py-1.5 text-2xs text-ink-muted hover:border-accent hover:text-accent lg:hidden"
          >
            <History size={13} aria-hidden />
            History
          </button>
        }
      />

      <div className="flex min-h-0 flex-1 gap-5">
        {/* --------------------------------------------------- conversations */}
        <aside className="hidden w-52 shrink-0 flex-col border-r border-rule pr-3 lg:flex">
          <NewQuestionButton onClick={startNew} />
          <div className="min-h-0 flex-1 overflow-y-auto">{history}</div>
          {spendLine && <div className="mt-2 border-t border-rule pt-2">{spendLine}</div>}
        </aside>

        {/* ---------------------------------------------------------- thread
            The scrolling region spans the full column and the *content* inside
            it carries the measure, not the other way round. Capping the
            scroller itself put its scrollbar at the edge of a 38rem box floating
            in the middle of a wide screen, which reads as a bug — a scrollbar
            belongs at the edge of the area it scrolls. Prose still sits at a
            readable width; only the track moved. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="qs-scroll min-h-0 flex-1 overflow-y-auto">
            {turns.length === 0 ? (
              <Measure className="h-full">
                <Welcome storeName={storeName} onPick={submit} />
              </Measure>
            ) : (
              <ul className="mx-auto w-full max-w-[38rem] px-1">
                {turns.map((turn) => (
                  <li
                    key={turn.id}
                    className={cn(
                      turn.role === 'user' &&
                        'mt-6 border-t border-rule pt-6 first:mt-0 first:border-0 first:pt-0',
                    )}
                  >
                    {turn.role === 'user' ? (
                      <Question text={turn.content} />
                    ) : turn.pending ? (
                      <Thinking />
                    ) : (
                      <Answer turn={turn} />
                    )}
                  </li>
                ))}
              </ul>
            )}
            <div ref={endRef} />
          </div>

          {/* Kept on the same measure as the thread above it, so the composer
              and the answers share an edge. */}
          <Measure>
            {error && (
              <p role="alert" className="mt-2 text-xs text-down">
                {error}
              </p>
            )}
            {notice && (
              <p role="status" className="mt-2 text-2xs text-ink-faint">
                {notice}
              </p>
            )}

            <Composer
              fieldRef={composerRef}
              value={question}
              onChange={setQuestion}
              onSubmit={() => submit(question)}
              onStop={() => abortRef.current?.abort()}
              pending={send.isPending}
              storeName={storeName}
            />

            <p className="mt-1.5 text-2xs text-ink-faint">
              Genie is AI and can make mistakes. Please double-check responses.
            </p>
          </Measure>
        </div>
      </div>

      {historyOpen && (
        <HistoryDrawer onClose={() => setHistoryOpen(false)}>
          <NewQuestionButton onClick={startNew} />
          <div className="min-h-0 flex-1 overflow-y-auto">{history}</div>
          {spendLine && <div className="mt-2 border-t border-rule pt-2">{spendLine}</div>}
        </HistoryDrawer>
      )}
    </div>
  );
}

/**
 * A spend figure that does not lie by rounding.
 *
 * A question costs a fraction of a penny, so `toFixed(2)` renders every early
 * month as "$0.00 used" — which reads as "nothing has happened" when in fact
 * plenty has. Below a penny, say so rather than showing zero.
 */
function formatSpend(usd: number): string {
  if (usd === 0) return '$0.00';
  if (usd < 0.01) return 'under $0.01';
  return `$${usd.toFixed(2)}`;
}

/**
 * The reading measure, applied to content rather than to containers.
 *
 * `px-1` rather than nothing, so a question's accent rule is not flush against
 * the scrollbar's track when the thread overflows.
 */
function Measure({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return <div className={cn('mx-auto w-full max-w-[38rem] px-1', className)}>{children}</div>;
}

function Header({ action }: { action?: React.ReactNode }): React.JSX.Element {
  return (
    <header className="mb-3 flex items-end justify-between gap-3">
      <div>
        <p className="qs-eyebrow">Genie</p>
        <h1 className="text-lg font-medium tracking-tight">Ask about your shop</h1>
      </div>
      {action}
    </header>
  );
}

function NewQuestionButton({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-2 flex items-center gap-1.5 rounded border border-rule px-2.5 py-1.5 text-xs text-ink-muted hover:border-accent hover:text-accent"
    >
      <Plus size={13} aria-hidden />
      New question
    </button>
  );
}

function ConversationList({
  conversations,
  activeId,
  onOpen,
  onDelete,
  deleting,
}: {
  conversations: Conversation[];
  activeId: string | null;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  deleting: boolean;
}): React.JSX.Element {
  if (conversations.length === 0) {
    return <p className="px-1 py-3 text-2xs text-ink-faint">Nothing asked yet.</p>;
  }

  return (
    <ul>
      {conversations.map((conversation) => {
        const active = conversation.conversationId === activeId;

        return (
          <li key={conversation.conversationId} className="group flex items-center gap-1">
            <button
              type="button"
              onClick={() => onOpen(conversation.conversationId)}
              className={cn(
                'min-w-0 flex-1 truncate rounded-r px-2 py-1.5 text-left text-2xs transition-colors',
                // A left rule rather than a filled pill: the console marks the
                // current thing with a hairline everywhere else, and a solid
                // block of accent in a narrow column reads as a button.
                active
                  ? 'border-l-2 border-accent bg-accent-soft font-medium text-accent'
                  : 'border-l-2 border-transparent text-ink-muted hover:bg-sunken hover:text-ink',
              )}
              title={conversation.title ?? 'Untitled'}
            >
              {conversation.title ?? 'Untitled'}
            </button>
            <button
              type="button"
              onClick={() => onDelete(conversation.conversationId)}
              disabled={deleting}
              /* Revealed on hover for a pointer, but always present for a thumb
                 and for the keyboard — an opacity-0 control is unreachable on a
                 touch screen, which has no hover to trigger it. */
              className="rounded p-1 text-ink-faint transition-opacity hover:text-down focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100"
              title="Delete"
            >
              <Trash2 size={11} aria-hidden />
              <span className="sr-only">Delete conversation</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The conversation list on a screen too narrow to spare a column for it.
 *
 * A slide-over rather than a route: opening history is a glance sideways, and
 * navigating away from a thread to find another one loses the one you had.
 */
function HistoryDrawer({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}): React.JSX.Element {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close history"
        className="absolute inset-0 bg-black/40"
      />
      <div
        role="dialog"
        aria-label="Conversations"
        aria-modal="true"
        /* The bottom bar is fixed above this, so a drawer that runs to the
           bottom of the viewport puts its last conversation and the spend line
           underneath it. The padding clears the bar and the home indicator; from
           `md` up there is no bar to clear. */
        className="qs-slide-in absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r border-rule bg-surface p-3 pb-[calc(env(safe-area-inset-bottom)+4.5rem)] shadow-float md:pb-3"
      >
        <div className="mb-2 flex items-center justify-between">
          <p className="qs-eyebrow">Conversations</p>
          <button
            type="button"
            onClick={onClose}
            className="qs-icon-button rounded p-1 text-ink-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={14} aria-hidden />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * A question, set as the heading of its exchange.
 *
 * The accent rule in the margin is what makes a long thread scannable — it is
 * the only thing on the page in that colour, so skimming back for "where did I
 * ask about credit?" is a matter of following the marks down the left edge. A
 * right-aligned bubble did the same job by floating text away from the margin,
 * which costs a column of whitespace on every exchange and looks like somebody
 * else's product.
 */
function Question({ text }: { text: string }): React.JSX.Element {
  return (
    <p className="qs-rise border-l-2 border-accent pl-3 text-sm font-medium text-ink">{text}</p>
  );
}

/**
 * What Genie shows while it is working.
 *
 * ── Why the labels are about time, not about progress ─────────────────────
 *
 * `/genie/ask` answers once, at the end: the tool steps arrive with the reply
 * and there is no stream to follow. So this cannot honestly say "Reading the
 * figures" or "Writing SQL" — it would be a guess dressed as telemetry, and
 * eventually a wrong one. What it *can* say truthfully is how long it has been,
 * which is the thing the reader actually wants to know when a wait runs long.
 *
 * The orb is a rotating conic gradient in the brand colours rather than a
 * spinner: a spinner reads as "the page is loading", and this is a model
 * composing an answer, which is a different kind of wait and worth looking
 * different.
 */
function Thinking(): React.JSX.Element {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Date.now() - started), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  const label =
    elapsed < 6_000
      ? 'Thinking'
      : elapsed < 20_000
        ? 'Still working'
        : 'This one is taking a while';

  return (
    <div className="qs-rise mt-3 flex items-center gap-2.5 pl-3" role="status" aria-live="polite">
      <span className="qs-thinking-orb block h-4 w-4 shrink-0" aria-hidden />
      <span className="qs-shimmer text-xs font-medium">{label}</span>
      <span className="flex gap-0.5" aria-hidden>
        <span className="qs-dot h-1 w-1 rounded-full bg-ink-faint" />
        <span className="qs-dot h-1 w-1 rounded-full bg-ink-faint" />
        <span className="qs-dot h-1 w-1 rounded-full bg-ink-faint" />
      </span>
      {/* Only once the wait is long enough that a number is reassuring rather
          than a stopwatch on a fast answer. */}
      {elapsed >= 6_000 && (
        <span className="qs-figure text-2xs text-ink-faint">{Math.round(elapsed / 1000)}s</span>
      )}
    </div>
  );
}

function Welcome({
  storeName,
  onPick,
}: {
  storeName: string;
  onPick: (question: string) => void;
}): React.JSX.Element {
  return (
    /* The block is centred in the column but its text is not: a console reads
       left, and centred body copy is the giveaway of a landing page. */
    <div className="flex h-full flex-col justify-center">
      <div>
        <h2 className="text-base font-medium tracking-tight">Ask about {storeName}</h2>
        <p className="mt-1 text-xs text-ink-muted">
          Genie reads trading figures, stock, credit and promotions, and shows its working on
          every answer.
        </p>

        <p className="qs-eyebrow mt-6 mb-1">Try asking</p>
        <ul className="border-t border-rule">
          {SUGGESTIONS.map((suggestion) => (
            <li key={suggestion}>
              {/* Rows, not cards. Four rounded boxes stacked in a column is the
                  house style of every AI product and none of this one — the
                  console lists things as ruled rows. */}
              <button
                type="button"
                onClick={() => onPick(suggestion)}
                className="group flex w-full items-center justify-between gap-3 border-b border-rule py-2 text-left text-xs text-ink-muted transition-colors hover:text-accent"
              >
                {suggestion}
                <ArrowRight
                  size={13}
                  aria-hidden
                  className="shrink-0 text-ink-faint transition-transform group-hover:translate-x-0.5 group-hover:text-accent"
                />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * The composer.
 *
 * A textarea, not an input: questions here run to a sentence or two and the
 * single-line field showed a sliding window of about eight words, so anything
 * longer could not be re-read before sending. It grows to five lines and then
 * scrolls.
 *
 * Enter sends only where there is a real keyboard. On a touch device the return
 * key is how you get a new line and there is no Shift to hold, so binding send
 * to it there would make a two-line question impossible to type.
 */
function Composer({
  fieldRef,
  value,
  onChange,
  onSubmit,
  onStop,
  pending,
  storeName,
}: {
  fieldRef: React.RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  pending: boolean;
  storeName: string;
}): React.JSX.Element {
  // Re-measured on every change: the value can also be cleared from outside
  // this component, when a question is sent.
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;

    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, 120)}px`;
  }, [value, fieldRef]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      /* One bordered box holding both controls, so the composer reads as a
         single object. Two separate boxes made the button look like it belonged
         to the page rather than to the field. */
      className="mt-3 flex items-end gap-1.5 rounded border border-rule bg-surface p-1.5 transition-colors focus-within:border-accent"
    >
      <textarea
        ref={fieldRef}
        rows={1}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || event.shiftKey) return;
          if (window.matchMedia('(pointer: coarse)').matches) return;

          event.preventDefault();
          onSubmit();
        }}
        placeholder={`Ask about ${storeName}…`}
        aria-label="Your question"
        className="max-h-30 flex-1 resize-none bg-transparent px-2 py-1.5 text-xs outline-none placeholder:text-ink-faint"
      />

      {pending ? (
        <button
          type="button"
          onClick={onStop}
          className="flex shrink-0 items-center gap-1.5 rounded border border-rule px-3 py-2 text-xs font-medium text-ink-muted hover:border-down hover:text-down"
        >
          <Square size={11} aria-hidden fill="currentColor" />
          Stop
        </button>
      ) : (
        <button
          type="submit"
          disabled={!value.trim()}
          className="flex shrink-0 items-center gap-1.5 rounded bg-accent px-3 py-2 text-xs font-medium text-ink-inverse hover:bg-accent-hover disabled:opacity-40"
        >
          <Send size={13} aria-hidden />
          Ask
        </button>
      )}
    </form>
  );
}

function CopyButton({ text }: { text: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1_600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={() => {
        // Unavailable outside a secure context, so this cannot be assumed.
        void navigator.clipboard?.writeText(text).then(
          () => setCopied(true),
          () => undefined,
        );
      }}
      className="flex items-center gap-1 text-2xs text-ink-faint hover:text-accent"
    >
      {copied ? <Check size={11} aria-hidden /> : <Copy size={11} aria-hidden />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/**
 * One answer, with its working available underneath.
 *
 * Collapsed by default: the working is what makes a figure checkable, but most
 * of the time the reader wants the answer. Open it and you see exactly which
 * tool ran with which arguments, and for the SQL fallback, the query itself.
 *
 * The answer itself is plain on the page rather than in a card. A thread of
 * cards is a stack of containers with one paragraph in each, which is heavy in
 * a console built from hairlines — and it spends the panel, this design's
 * strongest container, on prose. The panel is spent on the working instead,
 * where there is actually tabular data to hold.
 */
function Answer({ turn }: { turn: Turn }): React.JSX.Element {
  const [showWorking, setShowWorking] = useState(false);
  const steps = turn.steps ?? [];
  const totalMs = steps.reduce((sum, step) => sum + step.ms, 0);

  return (
    <div className="qs-rise mt-3 pl-3">
      <AnswerBody content={turn.content} />

      <div className="mt-2 flex items-center gap-4">
        {steps.length > 0 && (
          <button
            type="button"
            onClick={() => setShowWorking((open) => !open)}
            aria-expanded={showWorking}
            className="flex items-center gap-1 text-2xs text-ink-faint hover:text-accent"
          >
            <ChevronDown
              size={11}
              className={cn('transition-transform', showWorking && 'rotate-180')}
              aria-hidden
            />
            {/* Not `qs-figure`: that face is reserved for money and for columns
                that have to align, and setting the words in it spaced them out
                like a readout. */}
            {showWorking ? 'Hide' : 'Show'} working ({steps.length}{' '}
            {steps.length === 1 ? 'step' : 'steps'} · {totalMs}ms)
          </button>
        )}
        <CopyButton text={turn.content} />
      </div>

      {showWorking && steps.length > 0 && (
        <ol className="mt-2 grid gap-2 rounded border border-rule bg-sunken p-2.5">
          {steps.map((step, index) => (
            <li key={index} className="text-2xs">
              <div className="flex items-center gap-1.5">
                {step.sql ? (
                  <Database size={11} className="shrink-0 text-ink-faint" aria-hidden />
                ) : (
                  <Wrench size={11} className="shrink-0 text-ink-faint" aria-hidden />
                )}
                <span className={cn('qs-figure font-medium', !step.ok && 'text-down')}>
                  {step.tool}
                </span>
                <span className="qs-figure text-ink-faint">{step.ms}ms</span>
                {step.rowCount !== undefined && (
                  <span className="qs-figure text-ink-faint">{step.rowCount} rows</span>
                )}
              </div>

              {step.sql && (
                <pre className="mt-1 overflow-x-auto rounded border border-rule bg-surface p-2 font-mono text-2xs whitespace-pre-wrap">
                  {step.sql}
                </pre>
              )}

              {!step.sql && Object.keys(step.args).length > 0 && (
                <p className="mt-0.5 pl-4.5 text-ink-faint">
                  {Object.entries(step.args)
                    .filter(([, value]) => value !== undefined)
                    .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
                    .join(', ')}
                </p>
              )}

              {step.error && <p className="mt-0.5 pl-4.5 text-down">{step.error}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
